import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';

import { Permission } from '../../enums';
import type { FuelUser } from '../../models/user';
import { FuelUserManagementService } from '../../services/user-management-service';
import { UserManagementForm } from '../../store/forms/Admin/UserManagementForm';
import { applicationDataSelector } from '../../store/slices/application-data';
import useAppUtils from '../../utils/appUtils';
import { enhanceFetchedUser } from '../AdminContainer/enhanceUser';
import FuelDialogHeader from '../fuel-dialog-header/fuel-dialog-header';
import ApiKeys from './ApiKeys';
import UserForm from './UserForm';

interface IPros {
  open: boolean;
  onClose: (reloadListPage: boolean) => void;
  isExisting: boolean;
  userId: string;
  isProfile?: boolean;
  isSuperAdmin: boolean;
  permissions?: any;
}

const UserDetailsModal = ({
  open,
  onClose,
  isExisting,
  userId,
  isProfile = false,
  isSuperAdmin = false,
  permissions = null,
}: IPros) => {
  const { alert } = useAppUtils();
  const { ouData } = useSelector(applicationDataSelector);
  const [passwordRequestChangedSubmitted, setPasswordRequestChangedSubmitted] = useState(false);
  const { setProgress } = useProgress();

  const formValue = useForm<UserManagementForm>({
    defaultValues: {
      userInfo: {
        firstName: '',
        lastName: '',
        email: '',
      },
    },
  });

  const init = async () => {
    setProgress(true);
    const allRoles = await FuelUserManagementService.getAllRoles();
    // console.log('UserDetailsModal init', { allRoles, isSuperAdmin });
    let availableRoles: string[] = [];
    if (isSuperAdmin) {
      availableRoles = _.cloneDeep(allRoles);
    } else {
      // if user is not super admin remove ability to grant superadmin role
      availableRoles = _.cloneDeep(allRoles.filter((x) => x !== 'SuperAdmin'));
    }
    let newForm: UserManagementForm | null = null;
    if (isExisting && userId) {
      const userResponse = await FuelUserManagementService.getUser(userId);
      const processedUser = enhanceFetchedUser(userResponse, ouData.ouId, isSuperAdmin);
      newForm = UserManagementForm.createFromData(processedUser, availableRoles);
    } else {
      newForm = UserManagementForm.create(availableRoles);
    }
    formValue.reset(newForm);
    setProgress(false);
  };

  const handleClose = () => {
    // setOpen(false);
    onClose(false);
  };
  const handleResetPassword = async () => {
    const id = formValue.getValues('id');
    try {
      setProgress(true, 'Sending...');
      const response = await FuelUserManagementService.sendPasswordResetEmail(id);
      if (response === 200) {
        alert('Password Change Request submitted.');
        setPasswordRequestChangedSubmitted(true);
      }
    } catch (ex) {
      console.error('Unable to submit Password Change Request', { id, ex });
      alert('Unable to submit Password Change Request', 'error');
    } finally {
      setProgress(false);
    }
  };

  const submit: SubmitHandler<UserManagementForm> = async (form: UserManagementForm) => {
    const message = isExisting ? 'Updated' : 'User Created';
    setProgress(true, 'Saving...');
    try {
      const { availableRoles, originalUser, ...newPayload } = form;
      const fuelUser: FuelUser = newPayload;
      // copy old access rights so we don't lose old info
      fuelUser.ouAccessRights = originalUser && originalUser.ouAccessRights ? originalUser.ouAccessRights : {};
      const existingAccess = fuelUser.ouAccessRights[ouData.ouId];
      if (existingAccess) {
        existingAccess.enabled = form.enabled;
        existingAccess.roles = form.roles;
      } else {
        fuelUser.ouAccessRights[ouData.ouId] = { enabled: form.enabled, roles: fuelUser.roles };
      }
      const response = await FuelUserManagementService.upsert(fuelUser, ouData.ouId);
      if (response) {
        alert(message);
        onClose(true);
      }
    } catch (ex) {
      console.error('Unable to create user', ex);
      alert('Unable to create user', 'error');
    } finally {
      setProgress(false);
    }
  };
  useEffect(() => {
    init();
    return () => {
      // console.log("cleaned up");
      setPasswordRequestChangedSubmitted(false);
    };
  }, []);
  return (
    <div className="user-details-modal" id="user-details-modal">
      <FormProvider {...formValue}>
        <form onSubmit={formValue.handleSubmit(submit)}>
          <Dialog open={open} fullWidth maxWidth="sm">
            <FuelDialogHeader onClose={handleClose}>{isExisting ? 'Update' : 'Create User'}</FuelDialogHeader>
            <DialogContent dividers>
              {formValue && <UserForm isExisting={isExisting} isProfile={isProfile} isSuperAdmin={isSuperAdmin} />}
              {formValue && isProfile && permissions && permissions.has(Permission.ApiAccess) && (
                <ApiKeys formValue={formValue} permissions={permissions} />
              )}
            </DialogContent>
            <DialogActions>
              {isProfile && (
                <Button
                  autoFocus
                  color="success"
                  variant="contained"
                  onClick={handleResetPassword}
                  disabled={passwordRequestChangedSubmitted}
                >
                  Send Change Password Email
                </Button>
              )}
              <div style={{ flex: '1 0 0' }} />
              <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
                Cancel
              </Button>
              <Button color="success" variant="contained" autoFocus onClick={formValue.handleSubmit(submit)}>
                {isExisting ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      </FormProvider>
    </div>
  );
};

export default UserDetailsModal;
